import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { TranslocoModule } from '@jsverse/transloco';

/**
 * Reusable chat search / prompt input component.
 * Features:
 * - Rounded 32px border (#ebebeb) + subtle shadow
 * - Disabled state blocks input & send
 * - Placeholder label (also used for aria-label)
 * - Emits `search` event on Enter key (without Shift) or send button click
 * - Multi-line auto-expanding textarea (grows only as needed)
 * - Content projection slot for custom send icon (placed at right side)
 * - Width can be controlled via style bindings from parent
 * - Emits event shaped like a native event with target.value for backward compatibility
 */
@Component({
  selector: 'app-chat-search',
  standalone: true,
  imports: [TranslocoModule],
  templateUrl: './chat-search.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ['./chat-search.component.scss']
})
export class ChatSearchComponent implements AfterViewInit {
  /** Whether input is disabled */
  @Input() disabled: boolean = false;
  /** Placeholder / accessible label */
  @Input() label: string = 'Type message ...';
  /** Current value */
  @Input() value: string = '';
  /** Search event (payload compatible with existing usage: event.target['value']) */
  @Input() streaming: boolean = false;
  @Input() pipMode: boolean = false;

  @Output() search = new EventEmitter<{ target: { value: string } }>();
  @Output() interrupt = new EventEmitter<void>();


  @ViewChild('textarea') textarea!: ElementRef<HTMLTextAreaElement>;
  /** Captured single-line (baseline) height including paddings */
  private baselineHeight: number = 0;

  ngAfterViewInit(): void {
    if (this.textarea) {
      const el = this.textarea.nativeElement;
      // Compute baseline from line-height + vertical padding instead of scrollHeight (avoids placeholder wrap issues)
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || (parseFloat(styles.fontSize) * 1.3) || 18;
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      this.baselineHeight = lineHeight + paddingTop + paddingBottom;
      el.style.height = this.baselineHeight + 'px';
    }
    // Ensure correct initial size
    this.autoResize();
  }

  onInput(ev: Event) {
    if (this.disabled) { return; }
    const target = ev.target as HTMLTextAreaElement;
    this.value = target.value;
    this.autoResize();
  }

  onKeydown(ev: KeyboardEvent) {
    if (this.disabled) { return; }
    if (ev.key === 'Enter' && !ev.shiftKey) {
      // Prevent newline insertion; send instead
      ev.preventDefault();
      this.emitSearch();
    }
    // Allow Shift+Enter for manual newline if desired (not specified but harmless)
  }

  emitSearch() {
    if (this.disabled) { return; }
    const trimmed = this.value?.trim();
    if (!trimmed) { return; }
    // Emit shape expected by existing handler
    this.search.emit({ target: { value: this.value } });
    // Clear local value & textarea
    this.value = '';
    if (this.textarea) {
      this.textarea.nativeElement.value = '';
      this.autoResize();
    }
  }

  emitInterrupt() {
    this.interrupt.emit();
  }

  /** Auto-resize the textarea height based on content */
  private autoResize() {
    if (!this.textarea) { return; }
    const el = this.textarea.nativeElement;
    // Reset to baseline first
    el.style.height = this.baselineHeight + 'px';
    // Only grow if actual scrollHeight demands more (content wrapped or newlines)
    const needed = el.scrollHeight;
    if (needed > this.baselineHeight + 1) {
      el.style.height = Math.min(needed, 320) + 'px';
    }
  }

  /** Derive a consistent single line height in px (fallback to 1.4em approximation). */
  private singleLineHeight(): string {
    const lineHeight = window.getComputedStyle(this.textarea.nativeElement).lineHeight;
    if (lineHeight && lineHeight !== 'normal') {
      return lineHeight;
    }
    // Fallback: 1.4 * font-size
    const fontSize = window.getComputedStyle(this.textarea.nativeElement).fontSize || '16px';
    const numeric = parseFloat(fontSize) || 16;
    return (numeric * 1) + 'px';
  }
}
